import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { PageHeader, Loading, formatNumber } from "@/components/PageUI";
import { ArrowDownRight, ArrowUpRight, Plus, Radio, RefreshCw, Search, X, Heart, MessageCircle, Eye, ExternalLink, Users, Smile } from "lucide-react";
import { toast } from "sonner";

function timeAgo(iso) {
  if (!iso) return null;
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

function PlatformBadge({ platform }) {
  return <span className={`platform-badge ${platform}`} data-testid={`platform-badge-${platform}`}>{platform === "tiktok" ? "TikTok" : "Instagram"}</span>;
}

function MonitorCard({ m, onRefresh, busy }) {
  const refreshed = !!m.last_refreshed_at;
  return (
    <article className="monitor-card listen-card" data-testid={`monitor-${m.id}`}>
      <div className="monitor-head">
        <span><Radio size={13} />{refreshed ? "Live" : "Idle"}</span>
        {refreshed && m.last_refreshed_at && <b data-testid={`monitor-updated-${m.id}`}>Updated {timeAgo(m.last_refreshed_at)}</b>}
      </div>
      <h2>{m.topic}</h2>
      <p><Search size={14} />{m.query}</p>

      {!refreshed ? (
        <div className="listen-empty" data-testid={`monitor-empty-${m.id}`}>
          <p>Not yet refreshed. Pull live Instagram &amp; TikTok mentions for this monitor.</p>
          <button className="primary-btn full" onClick={() => onRefresh(m)} disabled={busy} data-testid={`monitor-refresh-${m.id}`}>
            <RefreshCw size={15} className={busy ? "spin" : ""} />{busy ? "Scanning the socials…" : "Refresh now · uses 2 searches"}
          </button>
        </div>
      ) : (
        <>
          <div className="mention-count">
            <strong data-testid={`monitor-mentions-${m.id}`}>{formatNumber(m.mentions)}</strong>
            <span>mentions</span>
            <div className={`trend ${m.change >= 0 ? "up" : "down"}`} data-testid={`monitor-trend-${m.id}`}>
              {m.change >= 0 ? <ArrowUpRight size={13} /> : <ArrowDownRight size={13} />}{Math.abs(m.change)}% this week
            </div>
          </div>

          {typeof m.sentiment === "number" && m.sentiment_breakdown && (
            <div className="listen-sentiment" data-testid={`monitor-sentiment-${m.id}`}>
              <div className="listen-sentiment-head">
                <span><Smile size={13} />{m.sentiment}% positive buzz</span>
                <small>AI tone estimate</small>
              </div>
              <div className="sentiment-split" title={`Positive ${m.sentiment_breakdown.positive}% · Neutral ${m.sentiment_breakdown.neutral}% · Negative ${m.sentiment_breakdown.negative}%`}>
                <i className="pos" style={{ width: `${m.sentiment_breakdown.positive}%` }} />
                <i className="neu" style={{ width: `${m.sentiment_breakdown.neutral}%` }} />
                <i className="neg" style={{ width: `${m.sentiment_breakdown.negative}%` }} />
              </div>
              <div className="sentiment-legend">
                <span><i className="pos" />{m.sentiment_breakdown.positive}% positive</span>
                <span><i className="neu" />{m.sentiment_breakdown.neutral}% neutral</span>
                <span><i className="neg" />{m.sentiment_breakdown.negative}% negative</span>
              </div>
            </div>
          )}

          {!!(m.top_creators && m.top_creators.length) && (
            <div className="listen-section">
              <h4><Users size={13} />Top creators</h4>
              <div className="listen-creators" data-testid={`monitor-creators-${m.id}`}>
                {m.top_creators.slice(0, 5).map((c, i) => (
                  <div className="listen-creator" key={`${c.platform}-${c.handle}-${i}`} data-testid={`creator-row-${m.id}-${i}`}>
                    {c.avatar ? <img src={c.avatar} alt={c.handle} referrerPolicy="no-referrer" onError={(e) => { e.target.style.display = "none"; }} /> : <div className="listen-avatar-fallback">{(c.name || c.handle || "?")[0]}</div>}
                    <div className="listen-creator-meta">
                      <strong>@{c.handle}</strong>
                      <span><PlatformBadge platform={c.platform} /> {c.posts} post{c.posts === 1 ? "" : "s"}</span>
                    </div>
                    <b>{formatNumber(c.engagement)}</b>
                  </div>
                ))}
              </div>
            </div>
          )}

          {!!(m.top_posts && m.top_posts.length) && (
            <div className="listen-section">
              <h4><Radio size={13} />Top posts</h4>
              <div className="listen-posts" data-testid={`monitor-posts-${m.id}`}>
                {m.top_posts.slice(0, 4).map((p, i) => (
                  <a className="listen-post" href={p.url || "#"} target="_blank" rel="noreferrer" key={i} data-testid={`post-row-${m.id}-${i}`}>
                    <div className="listen-post-top">
                      <PlatformBadge platform={p.platform} />
                      <span className="listen-post-top-right">
                        {p.tone && p.tone !== "neutral" && <span className={`tone-pill ${p.tone}`} data-testid={`post-tone-${m.id}-${i}`}>{p.tone === "positive" ? "Positive" : "Negative"}</span>}
                        <ExternalLink size={12} />
                      </span>
                    </div>
                    <p className="listen-post-cap">{p.caption || "(no caption)"}</p>
                    <div className="listen-post-stats">
                      <span><Heart size={11} />{formatNumber(p.likes || 0)}</span>
                      <span><MessageCircle size={11} />{formatNumber(p.comments || 0)}</span>
                      {!!p.views && <span><Eye size={11} />{formatNumber(p.views)}</span>}
                    </div>
                  </a>
                ))}
              </div>
            </div>
          )}

          {m.partial_errors && m.partial_errors.length > 0 && (
            <small className="listen-warn" data-testid={`monitor-partial-${m.id}`}>{m.partial_errors.join(" & ")} was unavailable this refresh.</small>
          )}

          <button className="secondary-btn full listen-refresh-again" onClick={() => onRefresh(m)} disabled={busy} data-testid={`monitor-refresh-${m.id}`}>
            <RefreshCw size={14} className={busy ? "spin" : ""} />{busy ? "Refreshing…" : "Refresh · uses 2 searches"}
          </button>
        </>
      )}
    </article>
  );
}

export default function Listening() {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ topic: "", query: "" });
  const [busyId, setBusyId] = useState(null);
  const qc = useQueryClient();
  const { data = [], isLoading } = useQuery({ queryKey: ["listening"], queryFn: () => api.get("/listening").then(r => r.data) });

  const create = async (e) => {
    e.preventDefault();
    await api.post("/listening", form);
    qc.invalidateQueries({ queryKey: ["listening"] });
    setOpen(false);
    setForm({ topic: "", query: "" });
    toast.success("Monitor created — hit Refresh to pull live data");
  };

  const refresh = async (m) => {
    setBusyId(m.id);
    const t = toast.loading(`Scanning Instagram & TikTok for “${m.topic}”…`);
    try {
      const r = await api.post(`/listening/${m.id}/refresh`);
      qc.invalidateQueries({ queryKey: ["listening"] });
      qc.invalidateQueries({ queryKey: ["usage"] });
      toast.success(`${formatNumber(r.data.mentions)} mentions found`, { id: t });
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error((d && (d.message || d)) || "Refresh failed — please try again shortly.", { id: t });
    } finally {
      setBusyId(null);
    }
  };

  return (
    <>
      <PageHeader eyebrow="Social listening" title="Hear the market before it moves." description="Track brands, competitors, and category language across Instagram and TikTok with real, on-demand data." action={<button className="primary-btn" onClick={() => setOpen(true)} data-testid="new-monitor-button"><Plus size={17} />New monitor</button>} />
      {isLoading ? <Loading /> : data.length === 0 ? (
        <div className="empty-state" data-testid="listening-empty-state"><Radio size={26} color="#00e5c7" /><p>No monitors yet. Create one to start listening.</p></div>
      ) : (
        <section className="monitor-grid">
          {data.map(m => <MonitorCard key={m.id} m={m} onRefresh={refresh} busy={busyId === m.id} />)}
        </section>
      )}
      {open && (
        <div className="modal-backdrop" data-testid="monitor-modal">
          <form className="modal small" onSubmit={create}>
            <div className="modal-head"><div><span className="eyebrow">NEW MONITOR</span><h2>Track a signal</h2></div><button type="button" className="icon-btn" onClick={() => setOpen(false)} data-testid="monitor-modal-close"><X /></button></div>
            <label>Monitor name<input required value={form.topic} onChange={e => setForm({ ...form, topic: e.target.value })} placeholder="e.g. Clean skincare" data-testid="monitor-topic-input" /></label>
            <label>Keywords and hashtags<textarea required rows="3" value={form.query} onChange={e => setForm({ ...form, query: e.target.value })} placeholder="skincare OR #cleanbeauty OR glowrecipe" data-testid="monitor-query-input" /></label>
            <p className="listen-hint">We'll turn these into hashtags and scan both platforms when you refresh.</p>
            <button className="primary-btn full" data-testid="monitor-create-submit"><Radio size={16} />Create monitor</button>
          </form>
        </div>
      )}
    </>
  );
}
